import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Workflow, FileSearch, Puzzle, ArrowRight, CheckCircle2, ExternalLink } from 'lucide-react';

const servicesData = [
  {
    id: 'assistants',
    title: 'Assistants IA & RAG métier',
    icon: Bot,
    color: '#3dd3ee',
    description: 'Des assistants IA spécialisés, connectés à vos données internes, capables de répondre de façon fiable à des questions complexes.',
    useCases: [
      'Assistant commercial (produits, concurrents, cas clients)',
      'Assistant interne (support, IT, juridique, RH)',
      'Recherche sectorielle et veille métier',
    ],
    implementation: [
      'RAG sur données privées (documents, bases, PDF, CRM)',
      'Recherche sémantique + génération contrôlée',
      'Réponses sourcées et traçables',
      'Intégration dans ChatGPT, Teams ou interfaces dédiées',
    ],
    projectLink: 'sectorai-rag',
    projectLabel: 'Voir le projet SectorAI',
  },
  {
    id: 'workflows',
    title: 'Automatisation de workflows',
    icon: Workflow,
    color: '#b4dc19',
    description: 'Nous repensons des processus existants pour réduire drastiquement les tâches manuelles à faible valeur ajoutée.',
    useCases: [
      'Traitement de flux documentaires volumineux',
      'Notices, fiches, dossiers techniques, bases éditoriales',
      'Chaînes de production hybrides IA / humain',
    ],
    implementation: [
      'Extraction et structuration automatique d\'informations',
      'Classification et enrichissement de contenus',
      'Boucles de validation humaine intégrées',
      'Gains de productivité mesurables (+60 % et plus)',
    ],
    projectLink: 'refonte-workflow-notices',
    projectLabel: 'Voir le cas Indexpresse',
  },
  {
    id: 'nlp',
    title: 'OCR & NLP avancé',
    icon: FileSearch,
    color: '#fad24b',
    description: 'Des pipelines NLP robustes pour lire, comprendre et structurer des contenus complexes.',
    useCases: [
      'Analyse de documents longs (PDF, rapports, notices)',
      'Structuration de bases de données textuelles',
      'Génération de synthèses et de contenus métiers',
    ],
    implementation: [
      'Extraction d\'informations clés (entités, chiffres, thématiques)',
      'Résumés contrôlés et contextualisés',
      'Classification multi-niveaux adaptée à vos référentiels',
    ],
    projectLink: 'enrichissement-base-llm',
    projectLabel: 'Voir le cas LLM commercial',
  },
  {
    id: 'integration',
    title: 'Intégration & adoption',
    icon: Puzzle,
    color: '#c8aff0',
    description: 'L\'IA n\'est utile que si elle est utilisée. Nous intégrons les solutions là où vos équipes travaillent déjà.',
    environments: [
      'Microsoft Teams (Copilot Studio)',
      'ChatGPT / LLM internes',
      'Backoffice métiers (Symfony, React, CRM, API)',
    ],
    guarantees: [
      'Intégration transparente',
      'Formation et accompagnement des équipes',
      'Suivi des usages et amélioration continue',
    ],
    projectLink: 'assistant-teams',
    projectLabel: 'Voir l\'intégration Teams',
  },
];

const Services = () => {
  const [expandedService, setExpandedService] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleProjectClick = (projectId) => {
    // Navigate to projects page with search query
    navigate(`/projets?project=${projectId}`);
  };

  const toggleExpand = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  return (
    <section id="services" ref={sectionRef} className="relative py-24 bg-[#f8f8f8] overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#3dd3ee]/20 to-transparent" />
        <div className="absolute top-20 right-10 w-64 h-64 bg-[#3dd3ee]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-64 h-64 bg-[#fad24b]/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-black/5 text-sm font-medium text-[#61525a] mb-6 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-[#3dd3ee] animate-pulse" />
            Solutions IA métier
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0a0a0b] mb-4">
            Prêtes pour la{' '}
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #3dd3ee, #c8aff0)' }}>
              production
            </span>
          </h2>
          <p className="text-lg text-[#61525a] max-w-2xl mx-auto">
            Des briques IA directement intégrables à vos processus existants
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-5">
          {servicesData.map((service, index) => {
            const Icon = service.icon;
            const isExpanded = expandedService === service.id;

            return (
              <div
                key={service.id}
                className={`transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div
                  className={`relative bg-white rounded-2xl border transition-all duration-500 overflow-hidden ${
                    isExpanded
                      ? 'border-transparent shadow-xl'
                      : 'border-black/5 hover:shadow-lg hover:border-transparent'
                  }`}
                >
                  {/* Top accent line */}
                  <div
                    className="absolute top-0 left-0 right-0 h-1"
                    style={{ backgroundColor: service.color }}
                  />

                  {/* Main Content - Always Visible */}
                  <div 
                    className="p-6 cursor-pointer"
                    onClick={() => toggleExpand(service.id)}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${service.color}15` }}
                      >
                        <Icon className="w-6 h-6" style={{ color: service.color }} />
                      </div>

                      {/* Title & Description */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-bold text-[#0a0a0b]">{service.title}</h3>
                          <div 
                            className={`w-6 h-6 rounded-full flex items-center justify-center transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                            style={{ backgroundColor: `${service.color}15` }}
                          >
                            <svg 
                              className="w-4 h-4" 
                              style={{ color: service.color }}
                              fill="none" 
                              viewBox="0 0 24 24" 
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                        <p className="text-[#61525a] text-sm leading-relaxed">
                          {service.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  <div 
                    className={`overflow-hidden transition-all duration-500 ${
                      isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-6 pb-6 pt-2 border-t border-black/5">
                      <div className="grid sm:grid-cols-2 gap-4">
                        {/* Use Cases or Environments */}
                        <div>
                          <h4 className="text-xs font-semibold text-[#0a0a0b] uppercase tracking-wider mb-3">
                            {service.environments ? 'Environnements' : 'Cas d\'usage'}
                          </h4>
                          <ul className="space-y-2">
                            {(service.useCases || service.environments)?.map((item, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <div 
                                  className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                                  style={{ backgroundColor: service.color }}
                                />
                                <span className="text-xs text-[#61525a]">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Implementation or Guarantees */}
                        <div>
                          <h4 className="text-xs font-semibold text-[#0a0a0b] uppercase tracking-wider mb-3">
                            {service.guarantees ? 'Nos garanties' : 'Ce que nous mettons en place'}
                          </h4>
                          <ul className="space-y-2">
                            {(service.implementation || service.guarantees)?.map((item, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <CheckCircle2 
                                  className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"
                                  style={{ color: service.color }}
                                />
                                <span className="text-xs text-[#61525a]">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Project Link */}
                      {service.projectLink && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProjectClick(service.projectLink);
                          }}
                          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all duration-300 hover:scale-105"
                          style={{ 
                            backgroundColor: `${service.color}15`,
                            color: service.color,
                          }}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          {service.projectLabel}
                          <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
